import { useMemo } from 'react';
import { Edge, Position } from 'reactflow';
import dagre from 'dagre';
import type { ScriptNode, ScriptNodeData } from '../components/BotFlowBuilder/types';

interface ScriptsData {
  conteudo: Record<string, string>;
  mentalidades: Record<string, string>;
  fatos_produto: string;
  persona?: string;
  girias?: string;
  abreviacoes?: string;
  emojis?: string;
}

/**
 * Hook para converter scripts do backend em nós do React Flow
 * com layout automático usando dagre (estilo n8n)
 */
export const useFlowNodes = (scripts: ScriptsData | undefined) => {
  return useMemo(() => {
    if (!scripts) return { nodes: [], edges: [] };

    const nodes: ScriptNode[] = [];
    const edges: Edge[] = [];

    // ===== SCRIPTS DE CONTEÚDO =====
    Object.entries(scripts.conteudo || {}).forEach(([nome, content]) => {
      nodes.push({
        id: `conteudo-${nome}`,
        type: 'scriptNode',
        position: { x: 0, y: 0 }, // Será recalculado pelo dagre
        data: {
          scriptType: 'conteudo',
          scriptName: nome,
          content: content || '',
          category: 'Scripts de Conteúdo',
          lineCount: (content || '').split('\n').length,
        },
      });
    });

    // ===== MENTALIDADES =====
    Object.entries(scripts.mentalidades || {}).forEach(([nome, content]) => {
      nodes.push({
        id: `mentalidade-${nome}`,
        type: 'scriptNode',
        position: { x: 0, y: 0 },
        data: {
          scriptType: 'mentalidade',
          scriptName: nome,
          content: content || '',
          category: 'Mentalidades',
          lineCount: (content || '').split('\n').length,
        },
      });
    });

    // ===== FATOS DO PRODUTO =====
    if (scripts.fatos_produto) {
      nodes.push({
        id: 'fatos_produto',
        type: 'scriptNode',
        position: { x: 0, y: 0 },
        data: {
          scriptType: 'fatos_produto',
          scriptName: 'fatos_produto',
          content: scripts.fatos_produto,
          category: 'Informações do Produto',
          lineCount: scripts.fatos_produto.split('\n').length,
        },
      });
    }

    // ===== PERSONA (opcional) =====
    if (scripts.persona) {
      nodes.push({
        id: 'persona',
        type: 'scriptNode',
        position: { x: 0, y: 0 },
        data: {
          scriptType: 'persona',
          scriptName: 'persona',
          content: scripts.persona,
          category: 'Configuração de Persona',
          lineCount: scripts.persona.split('\n').length,
        },
      });
    }

    // ===== GÍRIAS (opcional) =====
    if (scripts.girias) {
      nodes.push({
        id: 'girias',
        type: 'scriptNode',
        position: { x: 0, y: 0 },
        data: {
          scriptType: 'girias',
          scriptName: 'girias',
          content: scripts.girias,
          category: 'Linguagem',
          lineCount: scripts.girias.split('\n').length,
        },
      });
    }

    // ===== ABREVIAÇÕES (opcional) =====
    if (scripts.abreviacoes) {
      nodes.push({
        id: 'abreviacoes',
        type: 'scriptNode',
        position: { x: 0, y: 0 },
        data: {
          scriptType: 'abreviacoes',
          scriptName: 'abreviacoes',
          content: scripts.abreviacoes,
          category: 'Linguagem',
          lineCount: scripts.abreviacoes.split('\n').length,
        },
      });
    }

    // ===== EMOJIS (opcional) =====
    if (scripts.emojis) {
      nodes.push({
        id: 'emojis',
        type: 'scriptNode',
        position: { x: 0, y: 0 },
        data: {
          scriptType: 'emojis',
          scriptName: 'emojis',
          content: scripts.emojis,
          category: 'Emojis',
          lineCount: scripts.emojis.split('\n').length,
        },
      });
    }

    // ===== CRIAR EDGES LÓGICOS (scripts → mentalidades relacionadas) =====
    // Conectar scripts de conteúdo com mentalidades de mesmo nome
    Object.keys(scripts.conteudo || {}).forEach((nomeScript) => {
      const scriptId = `conteudo-${nomeScript}`;

      // Buscar mentalidade correspondente (mesmo nome)
      if (scripts.mentalidades && scripts.mentalidades[nomeScript]) {
        const mentalidadeId = `mentalidade-${nomeScript}`;
        edges.push({
          id: `edge-${scriptId}-${mentalidadeId}`,
          source: scriptId,
          target: mentalidadeId,
          type: 'smoothstep',
          animated: true,
          style: { stroke: '#ef4444', strokeWidth: 2 },
        });
      }
    });

    // ===== CRIAR HIERARQUIA LIMPA DE CONFIGURAÇÃO =====
    // Linguagem (Gírias, Abreviações, Emojis) → Persona → Fatos do Produto
    // Isso cria uma cadeia vertical limpa no topo do fluxo

    const linguagemScripts = ['girias', 'abreviacoes', 'emojis'];

    // Conectar Linguagem ao Persona (se existirem)
    linguagemScripts.forEach((scriptId) => {
      if (scripts[scriptId as keyof ScriptsData] && scripts.persona) {
        edges.push({
          id: `edge-${scriptId}-persona`,
          source: scriptId,
          target: 'persona',
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#10b981', strokeWidth: 2 },
        });
      }
    });

    // Conectar Persona ao Fatos do Produto (cadeia única, não spaghetti)
    if (scripts.persona && scripts.fatos_produto) {
      edges.push({
        id: 'edge-persona-fatos',
        source: 'persona',
        target: 'fatos_produto',
        type: 'smoothstep',
        animated: false,
        style: { stroke: '#8b5cf6', strokeWidth: 2 },
      });
    }

    // ===== APLICAR LAYOUT AUTOMÁTICO (dagre) =====
    const layoutedNodes = getLayoutedElements(nodes, edges);

    return { nodes: layoutedNodes, edges };
  }, [scripts]);
};

/**
 * Calcula layout automático usando dagre (algoritmo hierárquico)
 * Inspirado em n8n e agent builder da OpenAI
 */
function getLayoutedElements(nodes: ScriptNode[], edges: Edge[]): ScriptNode[] {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Configurar grafo com layout TB (Top to Bottom) - estilo Agent Kit
  dagreGraph.setGraph({
    rankdir: 'TB', // Top to Bottom (vertical)
    ranksep: 180, // Espaçamento vertical entre níveis (maior para melhor legibilidade)
    nodesep: 120, // Espaçamento horizontal entre nós (maior)
    marginx: 80,
    marginy: 80,
    align: 'UL', // Alinhar upper-left para melhor organização
    ranker: 'tight-tree', // Algorítmo mais compacto
  });

  // Adicionar nós ao grafo
  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, {
      width: 220, // Largura do nó customizado
      height: 120, // Altura do nó customizado
    });
  });

  // Adicionar edges ao grafo
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  // Executar layout
  dagre.layout(dagreGraph);

  // Aplicar posições calculadas aos nós
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      // Centralizar o nó na posição calculada
      position: {
        x: nodeWithPosition.x - 110, // largura / 2
        y: nodeWithPosition.y - 60, // altura / 2
      },
      // Configurar handles (conexões)
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    };
  });

  return layoutedNodes;
}
